import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import { billingConfirm, billingGetBalance } from '@api/billing';
import { Wrapper, Card, Title, Message } from './PaymentSuccess.styled';
import { STORAGE_KEYS } from '@constants/index';
import { trackFunnelEvent } from '@utils/datadog';

const STORAGE_KEY = STORAGE_KEYS.USER;

const PaymentSuccess: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirmedRef = useRef(false);
  /** Track the latest timer so we can cancel it on unmount. */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;

    if (!user?.token) return;

    const sessionId = searchParams.get('session_id');

    const activate = (updatedUser: typeof user) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
      trackFunnelEvent('os_first_payment', {
        plan_type: user.planType ?? 'unknown',
      });
      globalThis.location.href = '/settings/billing';
    };

    const confirm = async () => {
      if (unmountedRef.current) return;

      // 1. Try confirming via the session_id (works without webhooks)
      if (sessionId && !confirmedRef.current) {
        try {
          const { billingStatus } = await billingConfirm(user.token, sessionId);
          confirmedRef.current = true;
          if (billingStatus === 'ACTIVE') {
            activate({ ...user, billingStatus: 'ACTIVE' as const });
            return;
          }
        } catch {
          // Confirm failed — fall through to polling
        }
      }

      // 2. Fall back to polling the balance (for webhook-based activation)
      try {
        const { billingStatus } = await billingGetBalance(user.token);
        if (billingStatus === 'ACTIVE') {
          activate({ ...user, billingStatus: 'ACTIVE' as const });
          return;
        }
      } catch {
        // Ignore, keep polling
      }

      if (!unmountedRef.current) {
        timerRef.current = setTimeout(confirm, 2000);
      }
    };

    timerRef.current = setTimeout(confirm, 500);

    return () => {
      unmountedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [user, navigate, searchParams]);

  return (
    <ThemeProvider theme={darkTheme}>
      <Wrapper>
        <Card>
          <Title>Payment successful!</Title>
          <Message>Setting up your account... You&apos;ll be redirected shortly.</Message>
        </Card>
      </Wrapper>
    </ThemeProvider>
  );
};

export default PaymentSuccess;
